<template>
  <div class="flex flex-col">
    <b class="text-16px leading-18px title-divided self-start">
      Местоположение захоронения
    </b>
    <div class="grid-x grid-padding-x -mx-4 mt-6">
      <div class="cell auto flex flex-col space-y-6">
        <div class="input-group">
          <label>Страна</label>
          <Select
            class="bg-hint"
            :options="countries"
            :label="'name'"
            :value="location.country"
            @input="($event) => location.country = $event"
            placeholder="Выберите страну"/>
        </div>
        <div class="input-group">
          <label>Область</label>
          <Select
            class="bg-hint"
            :options="regions"
            :label="'name'"
            :value="location.region"
            :disabled="location.country === null"
            @input="($event) => location.region = $event"
            placeholder="Выберите область"/>
        </div>
        <div class="input-group">
          <label>Район</label>
          <Select
            class="bg-hint"
            :options="districts"
            :label="'name'"
            :value="location.district"
            :disabled="location.region === null"
            @input="($event) => location.district = $event"
            placeholder="Выберите район"/>
        </div>
        <div class="input-group">
          <label>Кладбище</label>
          <Select
            class="bg-hint"
            :options="cemeteries"
            :label="'name'"
            :value="location.cemetery"
            :disabled="location.district === null"
            @input="onCemeterySelect"
            placeholder="Выберите кладбище"/>
        </div>
        <Input
          label="Широта"
          placeholder="Введите широту"
          classes="bg-hint border-none"
          :inputmask="{
            alias: 'decimal',
            rightAlign: false,
            allowMinus: false,
            placeholder: '',
          }"
          v-model="location.location.lat"/>
        <Input
          label="Долгота"
          placeholder="Введите долготу"
          classes="bg-hint border-none"
          :inputmask="{
            alias: 'decimal',
            rightAlign: false,
            allowMinus: false,
            placeholder: '',
          }"
          v-model="location.location.lng"/>
        <Input
          label="Количество памятников"
          classes="bg-hint border-none"
          :disabled="true"
          v-model="length"
          v-if="type === 'family'"/>
      </div>
      <div class="cell auto flex flex-col space-y-6">
        <div class="input-group">
          <label>Размер захоронения</label>
          <Select
            class="bg-hint"
            :options="sizes"
            :value="location.size"
            @input="($event) => location.size = $event"
            placeholder="Выберите размер захоронения">
            <template v-slot:option="props">
              <span
                class="text-14px leading-16px"
                v-html="props.label"></span>
            </template>
            <template v-slot:selected-option="props">
              <span
                class="text-14px leading-16px"
                v-html="props.label"></span>
            </template>
          </Select>
        </div>
        <Input
          label="№ участка"
          placeholder="Введите номер участка"
          classes="bg-hint border-none"
          v-model="location.section"/>
        <Input
          label="№ могилы"
          placeholder="Введите номер могилы"
          classes="bg-hint border-none"
          :inputmask="{
            alias: 'numeric',
            rightAlign: false,
            allowMinus: false,
            placeholder: '',
          }"
          v-model="location.crypt"/>
        <div class="input-group">
          <label>Наличие ограды и ее тип</label>
          <Select
            class="bg-hint"
            :options="fences"
            :value="location.fence"
            @input="($event) => location.fence = $event"
            placeholder="Выберите наличие ограды и ее тип"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import { mapGetters, mapState, mapActions } from 'vuex';
import Input from '@/components/Input.vue';
import Select from '@/components/Select.vue';

export default {
  name: 'ObjectLocation',
  props: {
    type: {
      type: String,
      required: false,
      default: 'single',
    },
    value: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    total: {
      type: Number,
      required: false,
      default: 0,
    },
  },
  components: {
    Input,
    Select,
  },
  data() {
    return {
      location: {
        country: null,
        region: null,
        district: null,
        cemetery: null,
        fence: null,
        size: null,
        section: '',
        crypt: '',
        location: {
          lat: '',
          lng: '',
        },
        ...this.value,
      },
      length: this.total,
    };
  },
  computed: {
    ...mapState({
      countries: (state) => state.settings.countries,
    }),
    ...mapGetters({
      enumsGetter: 'settings/selectEnums',
      regionsGetter: 'settings/regions',
      districtsGetter: 'settings/districts',
      cemeteriesGetter: 'settings/cemeteries',
    }),
    sizes() {
      return this.enumsGetter('size');
    },
    fences() {
      return this.enumsGetter('fence');
    },
    regions() {
      const country = _.get(this.location.country, 'id');
      if (country) {
        return this.regionsGetter(country);
      }
      return [];
    },
    districts() {
      const region = _.get(this.location.region, 'id');
      if (region) {
        return this.districtsGetter(region);
      }
      return [];
    },
    cemeteries() {
      const district = _.get(this.location.district, 'id');
      if (district) {
        return this.cemeteriesGetter(district);
      }
      return [];
    },
  },
  methods: {
    ...mapActions({
      fetchRegions: 'settings/ACT_FETCH_REGIONS',
      fetchDistricts: 'settings/ACT_FETCH_DISTRICTS',
      fetchCemeteries: 'settings/ACT_FETCH_CEMETERIES',
    }),
    onCemeterySelect(e) {
      if (!e) {
        return;
      }
      this.location.cemetery = e;
      if (e.location) {
        this.location.location = e.location;
      }
    },
  },
  watch: {
    location: {
      handler(value) {
        this.$emit('input', value);
      },
      deep: true,
    },
    'location.country': {
      handler(value) {
        const id = _.get(value, 'id');
        if (id) {
          this.fetchRegions(id);
        }
        this.location.region = null;
      },
      deep: true,
    },
    'location.region': {
      handler(value) {
        const id = _.get(value, 'id');
        if (id) {
          this.fetchDistricts(id);
        }
        this.location.district = null;
      },
      deep: true,
    },
    'location.district': {
      handler(value) {
        const id = _.get(value, 'id');
        if (id) {
          this.fetchCemeteries(id);
        }
        this.location.cemetery = null;
      },
      deep: true,
    },
  },
};
</script>
